/**
 * CSS-based line highlighting utilities for optimal performance.
 * This approach avoids React re-renders by directly manipulating CSS classes.
 */

const HIGHLIGHT_DURATION = 3000; // 3 seconds
const HIGHLIGHT_CLASS = 'line-highlight-active';
const SHADOW_HIGHLIGHT_ATTR = 'data-mrge-shadow-line-highlight';
const SHADOW_HIGHLIGHT_STYLE_ID = 'mrge-shadow-line-highlight-style';
const SHADOW_HIGHLIGHT_DURATION_VAR = '--mrge-shadow-highlight-duration';
type FocusCodeSide = 'old' | 'new';

type ShadowHighlightTarget = HTMLElement & {
  __mrgeShadowHighlightTimeoutId?: number;
};

type LegacyHighlightTarget = HTMLElement & {
  __mrgeLineHighlightTimeoutId?: number;
};

function getLegacyLineIds(fileId: string, lineNumber: number): string[] {
  return [
    `${fileId}-line-new-${lineNumber}`, // Insert lines (additions)
    `${fileId}-line-${lineNumber}-${lineNumber}`, // Normal lines where old and new are the same
    `${fileId}-line-old-${lineNumber}`, // Delete lines
  ];
}

function getSideAwareLegacyLineIds(
  fileId: string,
  lineNumber: number,
  side?: FocusCodeSide,
): string[] {
  if (side === 'new') {
    return [`${fileId}-line-new-${lineNumber}`, `${fileId}-line-${lineNumber}-${lineNumber}`];
  }

  if (side === 'old') {
    return [`${fileId}-line-old-${lineNumber}`, `${fileId}-line-${lineNumber}-${lineNumber}`];
  }

  return getLegacyLineIds(fileId, lineNumber);
}

function normalizeV2CandidateSide(value: string | null): FocusCodeSide | null {
  if (value === 'new' || value === 'additions') {
    return 'new';
  }

  if (value === 'old' || value === 'deletions') {
    return 'old';
  }

  return null;
}

function resolveV2CandidateSide(candidate: HTMLElement): FocusCodeSide | null {
  const lineType = candidate.getAttribute('data-line-type');
  if (lineType === 'change-addition') {
    return 'new';
  }

  if (lineType === 'change-deletion') {
    return 'old';
  }

  const candidateSide = normalizeV2CandidateSide(candidate.getAttribute('data-side'));
  if (candidateSide) {
    return candidateSide;
  }

  if (candidate.closest('[data-additions]')) {
    return 'new';
  }

  if (candidate.closest('[data-deletions]')) {
    return 'old';
  }

  const ancestorWithSide = candidate.closest('[data-side]');
  if (!(ancestorWithSide instanceof HTMLElement)) {
    return null;
  }

  return normalizeV2CandidateSide(ancestorWithSide.getAttribute('data-side'));
}

function isMatchingV2Target(
  candidate: HTMLElement,
  lineNumber: number,
  side?: FocusCodeSide,
): boolean {
  const requestedLine = String(lineNumber);
  const line = candidate.getAttribute('data-line');
  const altLine = candidate.getAttribute('data-alt-line');
  const lineNumberAttr = candidate.getAttribute('data-line-number');
  const columnNumber = candidate.getAttribute('data-column-number');

  const lineMatches =
    line === requestedLine ||
    altLine === requestedLine ||
    lineNumberAttr === requestedLine ||
    columnNumber === requestedLine;

  if (!lineMatches) {
    return false;
  }

  if (!side) {
    return true;
  }

  return resolveV2CandidateSide(candidate) === side;
}

function getHighlightTargets(
  fileId: string,
  lineNumber: number,
  side?: FocusCodeSide,
): HTMLElement[] {
  const targets: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();

  const addTarget = (target: HTMLElement | null) => {
    if (!target || seen.has(target)) {
      return;
    }
    seen.add(target);
    targets.push(target);
  };

  // Legacy diff rows are table rows keyed by generated anchor ids.
  for (const elementId of getSideAwareLegacyLineIds(fileId, lineNumber, side)) {
    const element = document.getElementById(elementId);
    if (!(element instanceof HTMLElement)) {
      continue;
    }
    const row = element.closest('tr');
    addTarget(row instanceof HTMLElement ? row : null);
  }

  // PR v2 rows use data-line/data-alt-line attributes within the file section.
  const fileElement = document.getElementById(fileId);
  if (fileElement instanceof HTMLElement) {
    const requestedLine = String(lineNumber);
    const lineSelector = [
      `[data-line="${requestedLine}"]`,
      `[data-alt-line="${requestedLine}"]`,
      `[data-line-number="${requestedLine}"]`,
      `[data-column-number="${requestedLine}"]`,
    ].join(', ');

    const collectLineIndexTargets = (
      candidate: HTMLElement,
      queryScope: ParentNode,
    ): HTMLElement[] => {
      const lineIndex = candidate.getAttribute('data-line-index');
      if (!lineIndex) {
        return [];
      }

      return Array.from(
        queryScope.querySelectorAll(
          [
            `[data-line-index="${lineIndex}"][data-line]`,
            `[data-line-index="${lineIndex}"][data-alt-line]`,
            `[data-line-index="${lineIndex}"][data-line-number]`,
            `[data-line-index="${lineIndex}"][data-column-number]`,
            `[data-line-index="${lineIndex}"][data-no-newline]`,
          ].join(', '),
        ),
      ).filter((element): element is HTMLElement => element instanceof HTMLElement);
    };

    const rowCandidates = fileElement.querySelectorAll(lineSelector);

    rowCandidates.forEach((candidate) => {
      if (!(candidate instanceof HTMLElement)) {
        return;
      }
      if (!isMatchingV2Target(candidate, lineNumber, side)) {
        return;
      }
      addTarget(candidate);
      collectLineIndexTargets(candidate, fileElement).forEach((target) => {
        if (side && resolveV2CandidateSide(target) !== side) {
          return;
        }
        addTarget(target);
      });
    });

    // Pierre diff rows are rendered inside <diffs-container> shadow roots.
    const diffContainers = fileElement.querySelectorAll('diffs-container');
    diffContainers.forEach((container) => {
      if (!(container instanceof HTMLElement) || !container.shadowRoot) {
        return;
      }

      const shadowRoot = container.shadowRoot;
      const shadowCandidates = shadowRoot.querySelectorAll(lineSelector);

      shadowCandidates.forEach((candidate) => {
        if (!(candidate instanceof HTMLElement)) {
          return;
        }
        if (!isMatchingV2Target(candidate, lineNumber, side)) {
          return;
        }
        addTarget(candidate);
        collectLineIndexTargets(candidate, shadowRoot).forEach((target) => {
          if (side && resolveV2CandidateSide(target) !== side) {
            return;
          }
          addTarget(target);
        });
      });
    });
  }

  return targets;
}

function clearShadowHighlight(target: ShadowHighlightTarget) {
  if (target.__mrgeShadowHighlightTimeoutId !== undefined) {
    clearTimeout(target.__mrgeShadowHighlightTimeoutId);
    target.__mrgeShadowHighlightTimeoutId = undefined;
  }
  target.removeAttribute(SHADOW_HIGHLIGHT_ATTR);
  target.style.removeProperty(SHADOW_HIGHLIGHT_DURATION_VAR);
}

function ensureShadowHighlightStyles(root: ShadowRoot) {
  if (root.getElementById(SHADOW_HIGHLIGHT_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = SHADOW_HIGHLIGHT_STYLE_ID;
  style.textContent = `
    [${SHADOW_HIGHLIGHT_ATTR}="true"] {
      position: relative;
    }

    [${SHADOW_HIGHLIGHT_ATTR}="true"]::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(96, 165, 250, 0.35);
      pointer-events: none;
      animation: mrge-shadow-highlight-flash var(${SHADOW_HIGHLIGHT_DURATION_VAR}, ${HIGHLIGHT_DURATION}ms) ease-in-out;
    }

    @keyframes mrge-shadow-highlight-flash {
      0% {
        opacity: 0;
      }

      15% {
        opacity: 1;
      }

      85% {
        opacity: 0.9;
      }

      100% {
        opacity: 0;
      }
    }
  `;
  root.appendChild(style);
}

function applyShadowHighlight(target: ShadowHighlightTarget, duration: number) {
  const root = target.getRootNode();
  if (!(root instanceof ShadowRoot)) {
    return;
  }

  // Non-obvious: global page CSS does not penetrate shadow roots, so we inject
  // the flash style into each diff shadow root exactly once.
  ensureShadowHighlightStyles(root);

  clearShadowHighlight(target);
  target.style.setProperty(SHADOW_HIGHLIGHT_DURATION_VAR, `${duration}ms`);
  target.setAttribute(SHADOW_HIGHLIGHT_ATTR, 'true');

  target.__mrgeShadowHighlightTimeoutId = window.setTimeout(() => {
    clearShadowHighlight(target);
  }, duration);
}

function applyLineHighlight(target: HTMLElement, duration: number) {
  if (target.getRootNode() instanceof ShadowRoot) {
    applyShadowHighlight(target as ShadowHighlightTarget, duration);
    return;
  }

  // Remove any existing highlight class (in case of re-highlighting)
  target.classList.remove(HIGHLIGHT_CLASS);
  const legacyTarget = target as LegacyHighlightTarget;
  if (legacyTarget.__mrgeLineHighlightTimeoutId !== undefined) {
    clearTimeout(legacyTarget.__mrgeLineHighlightTimeoutId);
    legacyTarget.__mrgeLineHighlightTimeoutId = undefined;
  }

  // Force a reflow to restart the animation
  void target.offsetWidth;

  // Add the highlight class
  target.classList.add(HIGHLIGHT_CLASS);

  // Remove the class after the duration
  legacyTarget.__mrgeLineHighlightTimeoutId = window.setTimeout(() => {
    target.classList.remove(HIGHLIGHT_CLASS);
    legacyTarget.__mrgeLineHighlightTimeoutId = undefined;
  }, duration);
}

/**
 * Highlights a specific line in the PR diff view
 * @param fileId - The file identifier
 * @param lineNumber - The line number to highlight
 * @param side - The diff side to prefer when matching line targets
 * @param duration - How long to highlight (default: 3000ms)
 */
export function highlightLine(
  fileId: string,
  lineNumber: number,
  sideOrDuration?: FocusCodeSide | number,
  duration = HIGHLIGHT_DURATION,
) {
  const side = typeof sideOrDuration === 'string' ? sideOrDuration : undefined;
  const resolvedDuration = typeof sideOrDuration === 'number' ? sideOrDuration : duration;

  getHighlightTargets(fileId, lineNumber, side).forEach((target) => {
    applyLineHighlight(target, resolvedDuration);
  });
}

/**
 * Highlights multiple lines in the PR diff view
 * @param fileId - The file identifier
 * @param lineNumbers - Array of line numbers to highlight
 * @param side - The diff side to prefer when matching line targets
 * @param duration - How long to highlight (default: 3000ms)
 */
export function highlightLines(
  fileId: string,
  lineNumbers: number[],
  sideOrDuration?: FocusCodeSide | number,
  duration = HIGHLIGHT_DURATION,
) {
  const side = typeof sideOrDuration === 'string' ? sideOrDuration : undefined;
  const resolvedDuration = typeof sideOrDuration === 'number' ? sideOrDuration : duration;

  lineNumbers.forEach((lineNumber) => {
    highlightLine(fileId, lineNumber, side, resolvedDuration);
  });
}

/**
 * Clears all active highlights immediately
 */
export function clearHighlights() {
  const elements = document.querySelectorAll(`.${HIGHLIGHT_CLASS}`);
  elements.forEach((element) => {
    element.classList.remove(HIGHLIGHT_CLASS);
    const target = element as LegacyHighlightTarget;
    if (target.__mrgeLineHighlightTimeoutId !== undefined) {
      clearTimeout(target.__mrgeLineHighlightTimeoutId);
      target.__mrgeLineHighlightTimeoutId = undefined;
    }
  });

  const diffContainers = document.querySelectorAll('diffs-container');
  diffContainers.forEach((container) => {
    if (!(container instanceof HTMLElement) || !container.shadowRoot) {
      return;
    }

    const shadowHighlights = container.shadowRoot.querySelectorAll(`[${SHADOW_HIGHLIGHT_ATTR}]`);
    shadowHighlights.forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }
      clearShadowHighlight(element as ShadowHighlightTarget);
    });
  });
}
